import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getBaseUserId } from '../api/bitable';
import { billingApi, type AccessInfo } from './api';
import { BillingContext, type BillingContextValue } from './hooks';
import { BuyModal } from './BuyModal';

export function BillingProvider({ children }: { children: ReactNode }) {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);

  const refreshFor = useCallback((id: string) => {
    billingApi
      .access(id)
      .then(setAccess)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let active = true;
    getBaseUserId()
      .then((id) => {
        if (!active) return;
        setAccountId(id);
        refreshFor(id);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [refreshFor]);

  const refresh = useCallback(() => {
    if (accountId) refreshFor(accountId);
  }, [accountId, refreshFor]);

  const openBuy = useCallback(() => setBuyOpen(true), []);

  const gate = useCallback<BillingContextValue['gate']>(async () => {
    if (!accountId) return true; // fail-open: never block when we can't identify the user
    try {
      const r = await billingApi.consume(accountId);
      if (r.allowed) {
        setAccess((a) =>
          a
            ? {
                ...a,
                pro: r.pro,
                freeUsed: r.freeUsed ?? a.freeUsed,
                freeLimit: r.freeLimit ?? a.freeLimit,
                freeRemaining: r.freeRemaining ?? a.freeRemaining,
              }
            : a,
        );
        return true;
      }
      setBuyOpen(true);
      return false;
    } catch {
      return true; // transient network error → fail-open
    }
  }, [accountId]);

  const value = useMemo<BillingContextValue>(
    () => ({ accountId, access, refresh, openBuy, gate }),
    [accountId, access, refresh, openBuy, gate],
  );

  return (
    <BillingContext.Provider value={value}>
      {children}
      <BuyModal
        open={buyOpen}
        accountId={accountId}
        onClose={() => setBuyOpen(false)}
        onPaid={refresh}
      />
    </BillingContext.Provider>
  );
}
